// Admin → Users tab: growth by role, MFA coverage, dormant accounts table.
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import EChart from '../charts/EChart';
import { ChartCard, DataTable, EmptyState, ExportButton, exportToCsv, formatDateTime } from './shared';

const ROLE_LABEL = { admin: 'Admin', analyst: 'Analyst', simpleuser: 'Analyst', viewer: 'Viewer', consultant: 'Consultant' };
const roleName = (r) => ROLE_LABEL[r] || r;

const AdminUsers = ({ view, t }) => {
  const { usersGrowth, mfaByRole, dormantUsers = [] } = view;
  const safeDormant = Array.isArray(dormantUsers) ? dormantUsers : [];

  const growthOption = useMemo(() => {
    const rows = Array.isArray(usersGrowth) ? usersGrowth : [];
    if (!rows.length) return null;
    const roles = Array.from(new Set(rows.flatMap((r) => Object.keys(r.byRole || {}))));
    return {
      tooltip: { trigger: 'axis' },
      legend: { bottom: 0 },
      grid: { left: 40, right: 16, top: 24, bottom: 56 },
      xAxis: { type: 'category', boundaryGap: false, data: rows.map((r) => r.label || r.date) },
      yAxis: { type: 'value' },
      series: roles.map((role) => ({
        name: roleName(role),
        type: 'line',
        stack: 'total',
        smooth: true,
        showSymbol: false,
        areaStyle: { opacity: 0.35 },
        data: rows.map((r) => (r.byRole?.[role] ?? 0))
      }))
    };
  }, [usersGrowth]);

  const mfaOption = useMemo(() => {
    const rows = Array.isArray(mfaByRole) ? mfaByRole : [];
    if (!rows.length) return null;
    return {
      tooltip: { trigger: 'axis' },
      legend: { bottom: 0 },
      grid: { left: 40, right: 16, top: 24, bottom: 48 },
      xAxis: { type: 'category', data: rows.map((r) => roleName(r.role)) },
      yAxis: { type: 'value' },
      series: [
        { name: t('mfaEnabled'), type: 'bar', data: rows.map((r) => r.enabled || 0), itemStyle: { borderRadius: [4, 4, 0, 0] } },
        { name: t('mfaDisabled'), type: 'bar', data: rows.map((r) => r.disabled || 0), itemStyle: { borderRadius: [4, 4, 0, 0] } }
      ]
    };
  }, [mfaByRole, t]);

  const dormantColumns = useMemo(() => ([
    { key: 'name', label: t('colUser') },
    { key: 'email', label: t('colEmail') },
    { key: 'lastLogin', label: t('colLastLogin'), render: (r) => formatDateTime(r.lastLogin || r.lastActive) },
    { key: 'days', label: t('colInactiveDays'), render: (r) => r.inactiveDays ?? '—' }
  ]), [t]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title={t('growthTitle')} subtitle={t('growthSubtitle')}>
          {growthOption ? <EChart option={growthOption} height={300} /> : <EmptyState message={t('noGrowth')} />}
        </ChartCard>
        <ChartCard title={t('mfaTitle')} subtitle={t('mfaSubtitle')}>
          {mfaOption ? <EChart option={mfaOption} height={300} /> : <EmptyState message={t('noMfa')} />}
        </ChartCard>
      </div>

      <ChartCard
        title={t('dormantTitle')}
        subtitle={t('dormantSubtitle')}
        action={
          dormantUsers.length > 0 ? (
            <ExportButton label={t('exportCsv')} onClick={() => exportToCsv('dormant-users.csv', dormantColumns, safeDormant)} />
          ) : null
        }
      >
        <DataTable columns={dormantColumns} rows={safeDormant} emptyMessage={t('noDormant')} />
        <div className="mt-4">
          <Link to="/admin/users" className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-800">
            {t('manageUsers')}
            <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
      </ChartCard>
    </div>
  );
};

AdminUsers.propTypes = {
  view: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired
};

export default AdminUsers;
